import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Ticket, WhatsappLogo, CalendarCheck, ArrowUp, ArrowDown, TrendUp } from "@phosphor-icons/react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#002FA7", "#38BDF8", "#F59E0B", "#818CF8", "#34D399"];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [registrationData, setRegistrationData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const [statsRes, registrationsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/analytics/registrations`)
      ]);
      setStats(statsRes.data);
      setRegistrationData(registrationsRes.data.data || []);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  const seedVouchers = async () => {
    try {
      const res = await axios.post(`${API}/seed/vouchers`);
      toast.success(res.data.message);
      fetchDashboardData();
    } catch (error) {
      toast.error("Failed to seed vouchers");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border border-[#E2E8F0]">
              <CardContent className="p-6">
                <div className="h-20 skeleton rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const sourceData = stats ? [
    { name: "QR Code", value: stats.registrations_by_source.qr_code },
    { name: "ADSR", value: stats.registrations_by_source.adsr },
    { name: "Manual", value: stats.registrations_by_source.manual },
  ].filter(d => d.value > 0) : [];

  const voucherData = stats ? [
    { name: "Available", value: stats.vouchers.available, color: "#10B981" },
    { name: "Assigned", value: stats.vouchers.assigned, color: "#002FA7" },
    { name: "Redeemed", value: stats.vouchers.redeemed, color: "#8B5CF6" },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Customers */}
        <Card className="border border-[#E2E8F0] card-hover" data-testid="stat-total-customers">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#64748B] mb-1">Total Customers</p>
                <p className="font-heading text-3xl font-bold text-[#0A0A0A]">
                  {stats?.customers.total || 0}
                </p>
                <p className="text-sm text-[#10B981] flex items-center gap-1 mt-1">
                  <ArrowUp size={14} weight="bold" />
                  <span className="font-mono">{stats?.customers.today || 0}</span> today
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
                <Users size={24} className="text-[#002FA7]" weight="duotone" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* This Week Registrations */}
        <Card className="border border-[#E2E8F0] card-hover" data-testid="stat-week-registrations">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#64748B] mb-1">This Week</p>
                <p className="font-heading text-3xl font-bold text-[#0A0A0A]">
                  {stats?.customers.this_week || 0}
                </p>
                <p className="text-sm text-[#64748B] flex items-center gap-1 mt-1">
                  <TrendUp size={14} /> Registrations
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                <CalendarCheck size={24} className="text-[#F59E0B]" weight="duotone" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vouchers */}
        <Card className="border border-[#E2E8F0] card-hover" data-testid="stat-vouchers">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#64748B] mb-1">Vouchers</p>
                <p className="font-heading text-3xl font-bold text-[#0A0A0A]">
                  {stats?.vouchers.total || 0}
                </p>
                <p className="text-sm text-[#10B981] font-mono mt-1">
                  {stats?.vouchers.available || 0} available
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                <Ticket size={24} className="text-[#10B981]" weight="duotone" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Messages */}
        <Card className="border border-[#E2E8F0] card-hover" data-testid="stat-whatsapp">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#64748B] mb-1">WhatsApp</p>
                <p className="font-heading text-3xl font-bold text-[#0A0A0A]">
                  {stats?.whatsapp.total || 0}
                </p>
                <div className="flex gap-3 mt-1 text-sm font-mono">
                  <span className="text-[#10B981]">{stats?.whatsapp.inbound || 0} in</span>
                  <span className="text-[#002FA7]">{stats?.whatsapp.outbound || 0} out</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-lg bg-[#25D366]/10 flex items-center justify-center">
                <WhatsappLogo size={24} className="text-[#25D366]" weight="duotone" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Registration Trend */}
        <Card className="lg:col-span-2 border border-[#E2E8F0]" data-testid="registration-chart">
          <CardHeader className="border-b border-[#E2E8F0] pb-4">
            <CardTitle className="font-heading text-lg">Registration Trend</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {registrationData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={registrationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12, fill: "#64748B" }}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 12, fill: "#64748B" }} />
                  <Tooltip 
                    contentStyle={{ 
                      borderRadius: "8px", 
                      border: "1px solid #E2E8F0",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#002FA7" 
                    strokeWidth={2}
                    dot={{ fill: "#002FA7", strokeWidth: 2 }}
                    name="Total"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="qr_code" 
                    stroke="#38BDF8" 
                    strokeWidth={2}
                    dot={{ fill: "#38BDF8", strokeWidth: 2 }}
                    name="QR Code"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-[#64748B]">
                No registration data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Registration by Source */}
        <Card className="border border-[#E2E8F0]" data-testid="source-chart">
          <CardHeader className="border-b border-[#E2E8F0] pb-4">
            <CardTitle className="font-heading text-lg">By Source</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {sourceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={sourceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {sourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex flex-col items-center justify-center text-[#64748B]">
                <p className="mb-4">No customer data yet</p>
                <button
                  onClick={seedVouchers}
                  className="px-4 py-2 bg-[#002FA7] text-white rounded-lg text-sm hover:bg-[#001F6E] transition-colors"
                  data-testid="seed-vouchers-btn"
                >
                  Seed Test Vouchers
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Voucher Status */}
      <Card className="border border-[#E2E8F0]" data-testid="voucher-status-chart">
        <CardHeader className="border-b border-[#E2E8F0] pb-4">
          <CardTitle className="font-heading text-lg">Voucher Status Distribution</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {voucherData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={voucherData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: "#64748B" }} width={80} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {voucherData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-[#64748B]">
              No voucher data available. Click "Seed Test Vouchers" to add test data.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
